import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroupsStore } from 'src/store/groups';
import { useAuthStore } from 'src/store/auth';
import { RoleType } from 'src/constants';

const GroupCreate = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { loading, create, uploadImage } = useGroupsStore();

  // Check if user is admin, redirect to root if not
  useEffect(() => {
    if (currentUser && currentUser.role !== RoleType.Admin) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [host, setHost] = useState('');
  const [hostError, setHostError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  
  // Settings state
  const [textsWelcomeText, setTextsWelcomeText] = useState('');
  const [textsWelcomeTitle, setTextsWelcomeTitle] = useState('');
  const [emailsPrimary, setEmailsPrimary] = useState('');
  const [emailsSecondary, setEmailsSecondary] = useState('');
  const [emailsTertiary, setEmailsTertiary] = useState('');
  const [phonesPrimary, setPhonesPrimary] = useState('');
  const [phonesSecondary, setPhonesSecondary] = useState('');
  const [phonesTertiary, setPhonesTertiary] = useState('');
  const [colorsPrimary, setColorsPrimary] = useState('');
  const [colorsSecondary, setColorsSecondary] = useState('');
  const [colorsTertiary, setColorsTertiary] = useState('');
  const [colorsQuaternary, setColorsQuaternary] = useState('');
  const [colorsQuinary, setColorsQuinary] = useState('');
  const [colorsSenary, setColorsSenary] = useState('');
  const [colorsSeptenary, setColorsSeptenary] = useState('');
  const [colorsOctonary, setColorsOctonary] = useState('');
  const [colorsNonary, setColorsNonary] = useState('');
  const [colorsDenary, setColorsDenary] = useState('');
  // Current images loaded for creating group
  const [currentGroupImages, setCurrentGroupImages] = useState<{[key: string]: string}>({});

  const buildSettingsPayload = () => {
    const settings: any = {};
    const texts: any = {};
    const images: any = {};
    const emails: any = {};
    const phones: any = {};
    const colors: any = {};

    if (textsWelcomeText) texts.welcomeText = textsWelcomeText;
    if (textsWelcomeTitle) texts.welcomeTitle = textsWelcomeTitle;
    if (emailsPrimary) emails.primary = emailsPrimary;
    if (emailsSecondary) emails.secondary = emailsSecondary;
    if (emailsTertiary) emails.tertiary = emailsTertiary;
    if (phonesPrimary) phones.primary = phonesPrimary;
    if (phonesSecondary) phones.secondary = phonesSecondary;
    if (phonesTertiary) phones.tertiary = phonesTertiary;
    if (colorsPrimary) colors.primary = colorsPrimary;
    if (colorsSecondary) colors.secondary = colorsSecondary;
    if (colorsTertiary) colors.tertiary = colorsTertiary;
    if (colorsQuaternary) colors.quaternary = colorsQuaternary;
    if (colorsQuinary) colors.quinary = colorsQuinary;
    if (colorsSenary) colors.senary = colorsSenary;
    if (colorsSeptenary) colors.septenary = colorsSeptenary;
    if (colorsOctonary) colors.octonary = colorsOctonary;
    if (colorsNonary) colors.nonary = colorsNonary;
    if (colorsDenary) colors.denary = colorsDenary;

    if (Object.keys(texts).length) settings.texts = texts;
    if (Object.keys(images).length) settings.images = images;
    if (Object.keys(emails).length) settings.emails = emails;
    if (Object.keys(phones).length) settings.phones = phones;
    if (Object.keys(colors).length) settings.colors = colors;

    return Object.keys(settings).length ? settings : undefined;
  };

  // Host validation function - reactive validation
  const validateHost = (hostValue: string): boolean => {
    if (!hostValue.trim()) {
      setHostError('Host is required');
      return false;
    }

    const trimmedHost = hostValue.trim();

    // Check for protocol indicators - this is the main validation
    if (trimmedHost.includes('://') || trimmedHost.startsWith('http://') || trimmedHost.startsWith('https://')) {
      setHostError('Host must be in format: w/o http(s) protocol and valid url');
      return false;
    }

    // Basic URL validation regex for clean host
    const urlRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
    
    if (!urlRegex.test(trimmedHost)) {
      setHostError('Host must be in format: w/o http(s) protocol and valid url');
      return false;
    }

    setHostError('');
    return true;
  };

  const handleFileUpload = async (file: File, imageType: 'logo' | 'welcome' | 'emailHeader' | 'emailFooter') => {
    try {
      // For new groups, we'll store the file temporarily and upload after creation
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target?.result as string;
        if (previewUrl) {
          setCurrentGroupImages(prev => ({
            ...prev,
            [`new_${imageType}`]: previewUrl
          }));
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to process image:', error);
    }
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Validate host before proceeding
    if (!validateHost(host)) {
      return;
    }
    const settings = buildSettingsPayload();
    const result = await create({ name, description, host, status: 0, settings });
    
    // If we have images to upload, do it after group creation
    if (result && Object.keys(currentGroupImages).length > 0) {
      for (const [key, fileData] of Object.entries(currentGroupImages)) {
        if (key.startsWith('new_')) {
          const imageType = key.replace('new_', '') as 'logo' | 'welcome' | 'emailHeader' | 'emailFooter';
          try {
            // Convert data URL back to file
            const response = await fetch(fileData);
            const blob = await response.blob();
            const file = new File([blob], `${imageType}.jpg`, { type: blob.type });
            await uploadImage(result.id as string, file, imageType);
          } catch (error) {
            console.error(`Failed to upload ${imageType}:`, error);
          }
        }
      }
    }
    
    navigate('/admin-groups');
  };

  const handleBackToList = () => {
    navigate('/admin-groups');
  };

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      {/* Header */}
      <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 flex items-center rounded-full"
          >
            <img src="/icons/left.svg" alt="back" />
          </button>
          <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
            Create New Group
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6 max-md:px-2">
        <div className="bg-white-overlay big-corner p-6 max-md:p-4">
          <form onSubmit={handleCreate} className="grid gap-2 max-w-3xl">
            <input
              className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <div>
              <input
                className={`px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full ${hostError ? 'border-red-500' : ''}`}
                placeholder="Host"
                value={host}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setHost(newValue);
                  if (newValue.trim()) {
                    if (newValue.includes('://') || newValue.startsWith('http://') || newValue.startsWith('https://')) {
                      setHostError('Host must be in format: w/o http(s) protocol and valid url');
                    } else {
                      const urlRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
                      if (!urlRegex.test(newValue.trim())) {
                        setHostError('Host must be in format: w/o http(s) protocol and valid url');
                      } else {
                        setHostError('');
                      }
                    }
                  } else {
                    setHostError('');
                  }
                }}
                required
              />
              {hostError && (
                <p className="text-sm text-red-600 mt-1">{hostError}</p>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input id="toggle-settings" type="checkbox" checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />
              <label htmlFor="toggle-settings" className="text-sm">Show settings</label>
            </div>
            {showSettings && (
              <div className="grid gap-2 mt-2">
                <h3 className="text-lg">Settings</h3>
                <div className="grid gap-2">
                  <div className="grid gap-1">
                    <span className="text-sm">Texts</span>
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Welcome title" value={textsWelcomeTitle} onChange={(e) => setTextsWelcomeTitle(e.target.value)} />
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Welcome text" value={textsWelcomeText} onChange={(e) => setTextsWelcomeText(e.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <span className="text-sm">Images</span>
                    <div className="mt-3 p-3 bg-[var(--gray-50)] rounded border">
                      <h4 className="text-sm font-medium text-[var(--gray-700)] mb-2">Upload Files</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {(['logo', 'welcome'] as const).map((imageType) => {
                          const imageKey = `new_${imageType}`;
                          const currentImage = currentGroupImages[imageKey];
                          
                          return (
                            <div key={imageType} className="flex flex-col gap-1">
                              <label className="text-xs text-[var(--gray-600)] font-medium">{imageType}</label>
                              {currentImage ? (
                                <div className="relative">
                                  <img 
                                    src={currentImage} 
                                    alt={`${imageType} preview`}
                                    className="w-full h-20 object-cover rounded border mb-1"
                                  />
                                  <input 
                                    type="file" 
                                    accept="image/*"
                                    className="px-2 py-1 rounded text-xs border border-[var(--gray-300)]"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleFileUpload(file, imageType);
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded"
                                    onClick={() => {
                                      setCurrentGroupImages(prev => {
                                        const newImages = {...prev};
                                        delete newImages[imageKey];
                                        return newImages;
                                      });
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <input 
                                  type="file" 
                                  accept="image/*"
                                  className="px-2 py-1 rounded text-xs border border-[var(--gray-300)]"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleFileUpload(file, imageType);
                                    }
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-1">
                    <span className="text-sm">Emails</span>
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Primary email" value={emailsPrimary} onChange={(e) => setEmailsPrimary(e.target.value)} />
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Secondary email" value={emailsSecondary} onChange={(e) => setEmailsSecondary(e.target.value)} />
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Tertiary email" value={emailsTertiary} onChange={(e) => setEmailsTertiary(e.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <span className="text-sm">Phones</span>
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Primary phone" value={phonesPrimary} onChange={(e) => setPhonesPrimary(e.target.value)} />
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Secondary phone" value={phonesSecondary} onChange={(e) => setPhonesSecondary(e.target.value)} />
                    <input className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)]" placeholder="Tertiary phone" value={phonesTertiary} onChange={(e) => setPhonesTertiary(e.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <span className="text-sm">Colors</span>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Primary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsPrimary || '#000000'} onChange={(e) => setColorsPrimary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsPrimary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsPrimary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Secondary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsSecondary || '#000000'} onChange={(e) => setColorsSecondary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsSecondary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsSecondary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Tertiary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsTertiary || '#000000'} onChange={(e) => setColorsTertiary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsTertiary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsTertiary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Quaternary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsQuaternary || '#000000'} onChange={(e) => setColorsQuaternary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsQuaternary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsQuaternary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Quinary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsQuinary || '#000000'} onChange={(e) => setColorsQuinary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsQuinary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsQuinary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Senary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsSenary || '#000000'} onChange={(e) => setColorsSenary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsSenary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsSenary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Septenary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsSeptenary || '#000000'} onChange={(e) => setColorsSeptenary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsSeptenary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsSeptenary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Octonary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsOctonary || '#000000'} onChange={(e) => setColorsOctonary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsOctonary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsOctonary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Nonary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsNonary || '#000000'} onChange={(e) => setColorsNonary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsNonary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsNonary(v); }} />
                      </div>
                      <div>
                        <label className="flex items-center gap-2"><span className="w-24 text-sm">Denary</span><input type="color" className="h-10 w-14 p-0 bg-transparent" value={colorsDenary || '#000000'} onChange={(e) => setColorsDenary(e.target.value)} /></label>
                        <input className="mt-1 px-3 py-2 rounded-md bg-white text-[var(--gray-800)] placeholder-[var(--gray-400)] w-full" placeholder="#000000" value={colorsDenary} onChange={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = '#' + v; setColorsDenary(v); }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="submit"
                className={`id-card-button-primary status-done px-4 py-2 rounded-full w-fit ${(loading || !name.trim() || !description.trim() || !host.trim() || hostError) ? 'opacity-60 cursor-not-allowed' : ''}`}
                disabled={loading || !name.trim() || !description.trim() || !host.trim() || !!hostError}
              >
                Create Group
              </button>
              <button
                type="button"
                className="id-card-button-secondary px-4 py-2 rounded-full"
                onClick={handleBackToList}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GroupCreate;
